import TextField from '@components/v2/form/TextField/TextField';
import List from '@components/v2/ui/List/List';
import ListItem from '@components/v2/ui/List/ListItem';
import ListItemButton from '@components/v2/ui/List/ListItemButton';
import ListSubheader from '@components/v2/ui/List/ListSubheader';
import Typography from '@components/v2/ui/Typography/Typography';
import { Box } from '@mui/material';
import * as R from 'ramda';
import { useMemo, useState } from 'react';
import LangEnum from '../../../../lang/LangEnum';
import { ClarityEvents } from '../../../../lib/clarity/types/clarityEvents.types';
import { withEvent } from '../../../../lib/clarity/utils/captureEvent.utils';
import { useTranslation } from '../../../../modules/translation/TranslationContext';
import { useAbility } from '../../../../modules/users/AbilityContext';
import { valueMatchWithQuery } from '../../../../utils/string';
import { FilterTestIds } from '../../../../utils/testIds/Filter.testIds';
import {
  FilterProps,
  MultiDynamicOptionsFilter,
  PossibleFilters,
} from '../../../Crud/filters/sharedBetweenFilters/types';
import { HandleSelectFilterProps } from '../../contexts/filterV3.context.types';
import FilterOptionCreatorList from './FilterOptionCreatorList';
import { FILTER_LIST_ITEM_PADDING } from './constants';
interface FilterOptionListProps {
  onClick: (props: HandleSelectFilterProps) => void;
  options: (FilterProps & { groupBy: LangEnum })[];
}

type GroupedResult = LangEnum | (FilterProps & { groupBy: LangEnum });

const FilterOptionList = ({ onClick, options }: FilterOptionListProps) => {
  const { translate } = useTranslation();
  const ability = useAbility();
  const [searchText, setSearchText] = useState<string>('');

  const filteredOptions = useMemo(() => {
    const visibleColumns = options.filter((option) => {
      const available = valueMatchWithQuery(option.title, searchText) && !option.toHide;

      const resourceType = (option as MultiDynamicOptionsFilter).resourceType;
      const hasAbilityToRead = R.isNil(resourceType) ? true : ability.can('read', resourceType);

      return available && hasAbilityToRead;
    });

    return visibleColumns;
  }, [searchText, options, ability.can]);

  const groupBy = R.groupBy(({ groupBy }: { groupBy: LangEnum }) => groupBy);

  const groupedOptions = R.pipe(groupBy, R.toPairs, R.flatten)(filteredOptions) as GroupedResult[];

  const onClickOption = (props: HandleSelectFilterProps) => {
    onClick({ ...props, enableReturnToList: true });
  };

  return (
    <>
      <Box
        sx={{
          paddingTop: 0.5,
          paddingBottom: 1.25,
          paddingLeft: 1.25,
          paddingRight: 1,
          position: 'sticky',
          top: 0,
          zIndex: 1,
          bgcolor: 'background.paper',
        }}
      >
        <TextField
          fullWidth
          size="small"
          label={translate(LangEnum.GLOBAL_SEARCH)}
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          onFocus={withEvent(`${ClarityEvents.FOCUS_SEARCH_FILTER_TYPE}`, () => {
            return;
          })}
        />
      </Box>
      <Box sx={{ paddingLeft: 1.25, paddingRight: 1 }}>
        <List>
          {groupedOptions.length === 0 && (
            <ListItem sx={{ px: FILTER_LIST_ITEM_PADDING, py: 1, justifyContent: 'center' }}>
              <Typography component="span" color="text.secondary" variant="body2">
                {translate(LangEnum.GLOBAL_SEARCH_EMPTY_RESULTS)}
              </Typography>
            </ListItem>
          )}
          {groupedOptions.map((option, index) => {
            if (typeof option === 'string') {
              return (
                <ListSubheader
                  key={option}
                  sx={{ pt: 1 }}
                  testId={`${FilterTestIds.SECTION_PREFIX}${option}`}
                >
                  {translate(option)}
                </ListSubheader>
              );
            }

            if (option.type === PossibleFilters.OPTIONS_SELECTOR_CREATOR) {
              return (
                <FilterOptionCreatorList
                  key={option.title}
                  onClick={onClickOption}
                  option={option}
                />
              );
            }

            const isLastChild =
              groupedOptions[index + 1] && typeof groupedOptions[index + 1] === 'string';
            return (
              <ListItemButton
                key={option.title}
                onClick={withEvent(
                  `${ClarityEvents.LIST_FILTER_APPLIED_WITH_NAME} ${option.title}`,
                  () => onClickOption({ filter: option }),
                )}
              >
                <Typography component="span" color="textPrimary" gutterBottom={isLastChild}>
                  {option.title}
                </Typography>
              </ListItemButton>
            );
          })}
        </List>
      </Box>
    </>
  );
};

export default FilterOptionList;
